import { useMemo } from 'react';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useOrgKey } from '@/hooks/auth/useOrgKey';
import { useLiveDocsBlock } from '@reflex/plugin-docs/web/blocks/live';
import type {
  AgentModelProvider,
  AgentModelSupportAvailable,
  AgentModelSupportEntry,
  AgentModelSupportResponse,
  AgentModelEndpoint,
  AgentProviderEndpoint,
  DiscoveredModel,
  DiscoveryStatus,
  LaunchableAgentEntry,
} from '@reflex/shared';
import type { AgentType } from '@/lib/types';
import { agentModelSupportFixture } from './useAgentModelSupport.fixture';

/**
 * Reads the org's model-support catalog. Routed through the canonical
 * PRD-9a `useLiveDocsBlock` adapter so anonymous readers (and the
 * simulation shell on `/demos/:slug`) see a fixture rather than a network
 * call, while signed-in callers continue to read live workspace data.
 *
 * The return shape mirrors `UseQueryResult` enough that
 * `useAgentModelSupportHelpers` can destructure `{ data, isLoading,
 * isError, error }` exactly like before.
 */
export function useAgentModelSupport() {
  const orgKey = useOrgKey();
  const result = useLiveDocsBlock<AgentModelSupportResponse>({
    queryKey: queryKeys.agentModelSupport(orgKey),
    queryFn: api.getAgentModelSupport,
    fixture: agentModelSupportFixture,
    staleTime: 60_000,
  });
  return {
    data: result.data,
    isLoading: result.isLoading,
    isError: result.isFallback,
    error: result.error,
    isFallback: result.isFallback,
    source: result.source,
  };
}

export interface AgentModelSupportHelpers {
  isMatrixDriven: (agentType: AgentType) => boolean;
  getEntry: (agentType: AgentType) => AgentModelSupportEntry | null;
  getAvailableEntry: (agentType: AgentType) => AgentModelSupportAvailable | null;
  getProvidersForAgent: (agentType: AgentType) => AgentModelSupportAvailable['providers'];
  /** The matrix provider entry for an agent/provider, or `null` when unknown. */
  getProviderEntry: (agentType: AgentType, providerId: string | null) => AgentModelProvider | null;
  getEndpointsForAgent: (agentType: AgentType) => AgentModelEndpoint[];
  getEndpointsForProvider: (agentType: AgentType, providerId: string) => AgentModelEndpoint[];
  getDefaultEndpoint: (agentType: AgentType) => string | null;
  getDefaultProvider: (agentType: AgentType) => string | null;
  getDefaultModel: (agentType: AgentType) => string | null;
  getModelsForProvider: (agentType: AgentType, providerId: string) => DiscoveredModel[];
  isEndpointAvailable: (agentType: AgentType, endpointId: string) => boolean;
  getEndpointAvailabilityMessage: (agentType: AgentType, endpointId: string) => string | null;
  getDiscoveredModels: (agentType: AgentType) => DiscoveredModel[];
  getDiscoveryStatus: (agentType: AgentType) => DiscoveryStatus | null;
  /**
   * Resolve a model id to its owning provider, scoped to one agent type.
   *
   * Resolution order:
   * 1. Discovery catalog (`discoveredModels[].providerId`) — populated from
   *    each plugin's `multiModel.providers` and the keys the user configured.
   * 2. The agent's default provider from the plugin manifest — used when
   *    discovery hasn't surfaced this specific id (custom free-text models,
   *    stale catalog).
   * 3. Empty string when the agent isn't multi-model. Callers gate on
   *    `isMatrixDriven` before reading this for that reason.
   *
   * No model-id prefix heuristic — the agent's manifest is the source of
   * truth for what provider its models route to. See `deriveProviderFromModelId`
   * for the unscoped fallback used by display-only chips that lack an agent
   * context.
   */
  getProviderForModel: (agentType: AgentType, modelId: string) => string;
  /**
   * Effective agent-level availability for an agent type (PRD-24). Returns `true`
   * when the server hasn't sent a `launchableAgents` list (older deployments),
   * so launch UIs default to showing every known type.
   */
  isAgentTypeEnabled: (agentType: AgentType) => boolean;
}

export function useAgentModelSupportHelpers(): AgentModelSupportHelpers & {
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  defaultAgentType: string | undefined;
  launchableAgents: LaunchableAgentEntry[] | undefined;
} {
  const { data, isLoading, isError, error } = useAgentModelSupport();

  const helpers = useMemo<AgentModelSupportHelpers>(() => {
    const agents = data?.agents ?? {};
    const launchableAgents = data?.launchableAgents;
    const launchableIndex = new Map<string, LaunchableAgentEntry>();
    if (launchableAgents) {
      for (const entry of launchableAgents) {
        launchableIndex.set(entry.agentType, entry);
      }
    }

    function getEntry(agentType: AgentType): AgentModelSupportEntry | null {
      const entry = agents[agentType];
      return entry ?? null;
    }

    function getAvailableEntry(agentType: AgentType): AgentModelSupportAvailable | null {
      const entry = getEntry(agentType);
      if (!entry || entry.status !== 'available') return null;
      return entry;
    }

    function isMatrixDriven(agentType: AgentType): boolean {
      return getAvailableEntry(agentType) !== null;
    }

    function getProvidersForAgent(agentType: AgentType): AgentModelSupportAvailable['providers'] {
      return getAvailableEntry(agentType)?.providers ?? [];
    }

    function getProviderEntry(
      agentType: AgentType,
      providerId: string | null,
    ): AgentModelProvider | null {
      if (!providerId) return null;
      return getProvidersForAgent(agentType).find((p) => p.id === providerId) ?? null;
    }

    function getEndpointsForAgent(agentType: AgentType): AgentModelEndpoint[] {
      return getAvailableEntry(agentType)?.endpoints ?? [];
    }

    function getEndpointsForProvider(
      agentType: AgentType,
      providerId: string,
    ): AgentModelEndpoint[] {
      const entry = getAvailableEntry(agentType);
      if (!entry) return [];

      const validEndpointIds = new Set(
        entry.providerEndpoints
          .filter((pe: AgentProviderEndpoint) => pe.providerId === providerId)
          .map((pe: AgentProviderEndpoint) => pe.modelEndpointId),
      );

      return entry.endpoints.filter((endpoint: AgentModelEndpoint) =>
        validEndpointIds.has(endpoint.id),
      );
    }

    function getDefaultEndpoint(agentType: AgentType): string | null {
      return getAvailableEntry(agentType)?.defaultEndpoint ?? null;
    }

    function getDefaultProvider(agentType: AgentType): string | null {
      return getAvailableEntry(agentType)?.defaultProvider ?? null;
    }

    function getDefaultModel(agentType: AgentType): string | null {
      return getAvailableEntry(agentType)?.defaultModel ?? null;
    }

    function getModelsForProvider(agentType: AgentType, providerId: string): DiscoveredModel[] {
      const discoveredModels = getDiscoveredModels(agentType);
      return discoveredModels.filter((m) => m.providerId === providerId);
    }

    function isEndpointAvailable(agentType: AgentType, endpointId: string): boolean {
      const entry = getAvailableEntry(agentType);
      if (!entry) return false;
      const endpoint = entry.endpoints.find((e: AgentModelEndpoint) => e.id === endpointId);
      return endpoint?.availability.available ?? false;
    }

    function getEndpointAvailabilityMessage(
      agentType: AgentType,
      endpointId: string,
    ): string | null {
      const entry = getAvailableEntry(agentType);
      if (!entry) return null;
      const endpoint = entry.endpoints.find((e: AgentModelEndpoint) => e.id === endpointId);
      if (!endpoint || endpoint.availability.available) return null;

      const { reasons, missingEnvVars } = endpoint.availability;
      if (missingEnvVars.length > 0) {
        return `Missing credentials: ${missingEnvVars.join(', ')}`;
      }
      if (reasons.includes('credential_status_unknown')) {
        return 'Credential status unknown';
      }
      return 'Endpoint unavailable';
    }

    function getDiscoveredModels(agentType: AgentType): DiscoveredModel[] {
      return getAvailableEntry(agentType)?.discoveredModels ?? [];
    }

    function getDiscoveryStatus(agentType: AgentType): DiscoveryStatus | null {
      return getAvailableEntry(agentType)?.discoveryStatus ?? null;
    }

    function getProviderForModel(agentType: AgentType, modelId: string): string {
      const discovered = getDiscoveredModels(agentType).find((m) => m.id === modelId);
      if (discovered) return discovered.providerId;
      return getDefaultProvider(agentType) ?? '';
    }

    function isAgentTypeEnabled(agentType: AgentType): boolean {
      // Fail open when the server hasn't sent a `launchableAgents` list — older
      // deployments shouldn't silently hide agent types from the Launch dialog.
      if (!launchableAgents) return true;
      const entry = launchableIndex.get(agentType);
      if (!entry) return true;
      return entry.enabled;
    }

    return {
      isMatrixDriven,
      getEntry,
      getAvailableEntry,
      getProvidersForAgent,
      getProviderEntry,
      getEndpointsForAgent,
      getEndpointsForProvider,
      getDefaultEndpoint,
      getDefaultProvider,
      getDefaultModel,
      getModelsForProvider,
      isEndpointAvailable,
      getEndpointAvailabilityMessage,
      getDiscoveredModels,
      getDiscoveryStatus,
      getProviderForModel,
      isAgentTypeEnabled,
    };
  }, [data]);

  return {
    ...helpers,
    isLoading,
    isError,
    error,
    defaultAgentType: data?.defaultAgentType,
    launchableAgents: data?.launchableAgents,
  };
}
